// frontend/src/pages/GridPlannerPage.js
//
// /tools/instagram-grid-planner
//
// MEASURED 2026-08-20. `instagram feed planner` 880/mo KD 12 + `instagram grid
// planner` 720/mo KD 5 = ~1,600/mo. SERP-checked and they share a SERP: Apple's
// Preview app listing, oneupapp.io/tools/instagram-grid-planner,
// hopperhq.com/tools/instagram-grid-planner, thepreviewapp.com, a Reddit thread.
// One page, and the URL matches the path both competitors use.
//
// WALL 0 — recorded in the feature queue and the softest target left. This is
// also the SMALLEST volume we have built for, and that is deliberate: it is the
// closest tool on the list to the actual product, because a grid planner IS a
// scheduling preview. Expect it to convert better than it traffics.
//
// TWO SIBLINGS DELIBERATELY EXCLUDED, both bigger:
//   `grid maker for instagram` 6,600/mo KD 17 — a different tool entirely. Its
//     SERP is mysocialboutique, designhub, carouselmaker, splitimage: IMAGE
//     SPLITTERS that cut one picture into nine tiles. Buildable later, not here.
//   `instagram mockup` 1,900/mo KD 0 — Figma templates, Adobe Stock, Etsy.
//     Design assets, not a planner.
//
// AI OVERVIEW VOLATILITY, worth recording: an AIO was present on `instagram feed
// planner` on 2026-08-19 and absent on 2026-08-20. Treat single AIO observations
// as a snapshot, not a property of the keyword.
//
// THE ANGLE IS THE 4:5 CHANGE. Instagram moved the profile grid from square to
// 4:5 portrait on mobile in early 2025; desktop still shows squares. Most grid
// planners still render square-only, which previews a layout most of the
// audience never sees. Verified 2026-08-20 against Buffer's grid guide,
// Fstoppers and SocialBee's 2026 sizing guide, which agree on both the ratio and
// the desktop/mobile split.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Button, Accordion, Image } from 'react-bootstrap'
import { CheckCircleFill } from 'react-bootstrap-icons'

import Seo from '../components/Seo'
import { getSeoMeta } from '../data/seoMeta'
import GridPlanner from '../components/GridPlanner'

const URL = '/tools/instagram-grid-planner'
const HERO = '/assets/images/articles/grid-planner.webp'

// Drives the visible accordion; the FAQPage schema in seo-meta.json was
// generated from it. See LinkedInFormatterPage.js for the regeneration command.
export const FAQS = [
  ['What is an Instagram grid planner?',
   'A tool for arranging your next posts in a preview of your profile before you publish any of them. You see the layout the way a visitor to your profile will, so you can spot a clash of colour or three near-identical photos in a row while it is still easy to change.'],
  ['Is the Instagram grid square or portrait now?',
   'Both, depending on where it is viewed. Instagram changed the profile grid to a 4:5 portrait ratio in the mobile app in early 2025, but the web version still shows 1:1 squares. That means your profile genuinely looks different on a phone and on a desktop, and an image composed for one crop can be spoiled by the other. The planner above shows both — use the toggle.'],
  ['What size should my Instagram photos be?',
   '1080 × 1350 pixels, which is 4:5. That fills the portrait grid on mobile without cropping and still centre-crops acceptably to a square on desktop. Uploading a square 1080 × 1080 image is the opposite trade: perfect on the web, letterboxed or cropped in the app where most people will see it.'],
  ['Where do new posts appear in the grid?',
   'Top left, pushing everything else down one place. This is why planning matters at all — a single new post shifts the entire layout, so a carefully arranged three-by-three becomes something else the moment you publish again. The planner adds new images to the front for exactly this reason.'],
  ['Do my images get uploaded anywhere?',
   'No. The planner runs entirely in your browser and the images never leave your device. There is no account, no upload and no limit on how many you use.'],
  ['Does the grid layout actually matter?',
   'Less than it used to, and less than the people selling grid tools suggest. Most views now come through the feed and Reels rather than profile visits. Where it does matter is the moment someone considers following you — they check the profile, and nine posts is what they judge you on. That is worth twenty minutes of planning, not twenty hours.'],
  ['Can I plan a feed without an Instagram account?',
   'Yes. Nothing here connects to Instagram, so you can plan a grid for an account that does not exist yet — which is genuinely useful when you are setting one up and want to see how the first nine posts will look together.'],
  ['How many posts should I plan ahead?',
   'Nine is the natural unit because it is what fills the visible grid on most screens, and it is roughly two to three weeks of posting for most accounts. Planning further ahead than that tends to collide with whatever actually happens in your business.'],
]

export default function GridPlannerPage () {
  return (
    <>
      <Seo {...getSeoMeta(URL)} image={HERO} />

      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-4'>
            <Col lg={7}>
              <h1 className='display-5 fw-bold mb-3'>Instagram grid planner</h1>
              <p className='lead mb-0'>
                Arrange your next posts and see the profile before you publish — in both the
                4:5 portrait grid the app now shows and the square grid the web still uses.
              </p>
            </Col>
            <Col lg={5}>
              <Image src={HERO} alt='A photographer arranging printed photos on a table' fluid rounded
                     loading='eager' width={1600} height={1066} className='shadow-sm' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── THE ANSWER FIRST ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <p className='fs-5'>
            <strong>The Instagram grid is no longer square.</strong> Instagram moved the profile
            grid to a <strong>4:5 portrait</strong> ratio in the mobile app in early 2025. The web
            version still shows 1:1 squares. Your profile therefore looks different depending on
            where someone opens it, and an image composed to survive one crop can be ruined by
            the other.
          </p>
          <p className='text-muted fs-5 mb-0'>
            Most grid planners still preview squares only. This one shows both, flags how much of
            each image the current crop is discarding, and never uploads anything — the images
            stay on your device.
          </p>
        </Container>
      </section>

      {/* ───────── THE TOOL ───────── */}
      <section className='pb-5'>
        <Container fluid='lg' style={{ maxWidth: 1000 }}>
          <GridPlanner />
        </Container>
      </section>

      {/* ───────── THE NUMBERS ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 900 }}>
          <h2 className='fw-bold mb-4'>Instagram grid sizes that matter</h2>
          <div className='table-responsive'>
            <table className='table table-bordered align-middle bg-white mb-0'>
              <tbody>
                {[
                  ['Grid on mobile', '4:5 portrait', 'Changed from square in early 2025.'],
                  ['Grid on desktop', '1:1 square', 'Unchanged — hence the mismatch.'],
                  ['Best upload size', '1080 × 1350', 'Fills the portrait grid with no crop.'],
                  ['Square upload', '1080 × 1080', 'Perfect on web, cropped or padded in the app.'],
                  ['Where new posts land', 'Top left', 'Everything else shifts down one place.'],
                  ['Visible grid', 'Usually 9 posts', 'Three rows is what a visitor judges.'],
                  ['Safe area', 'Centre of the frame', 'Both crops keep the middle; edges vary.'],
                ].map(([label, value, note]) => (
                  <tr key={label}>
                    <td className='fw-semibold' style={{ width: '26%' }}>{label}</td>
                    <td className='text-primary fw-semibold' style={{ width: '22%' }}>{value}</td>
                    <td className='text-muted'>{note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Container>
      </section>

      {/* ───────── DEPTH ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          {[
            ['Compose for the centre, because the edges are negotiable',
             'The only part of an image guaranteed to survive both crops is the middle. A 4:5 upload keeps its full height in the app and loses the top and bottom on the web; a square keeps everything on the web and gets cropped or padded in the app. Put the face, the product or the text in the central square and the picture works in both places without you having to choose.'],
            ['One new post rearranges everything',
             'Posts enter at the top left and push the rest down a place, so a grid you arranged carefully lasts exactly until you publish again. This is the actual argument for planning in threes: a row survives as a unit as it moves down the grid, while a clever nine-image mosaic is destroyed by the next thing you post.'],
            ['The grid matters less than the people selling grid tools say',
             'Most of your views now come through the feed and Reels, not profile visits. Where the grid does earn its keep is the specific moment someone is deciding whether to follow you — they open the profile, and the top nine posts are the entire basis of that judgement. That is worth twenty minutes of arranging, not twenty hours of building a mosaic.'],
            ['Plan about three weeks ahead',
             'Nine posts fills the visible grid and is roughly two to three weeks of posting for most accounts. Beyond that, planning collides with whatever actually happens in the business — the thing you did not know you would be announcing, the photo you did not know you would take. A planned grid that has to be torn up is worse than no plan.'],
          ].map(([heading, body]) => (
            <div className='mb-4' key={heading}>
              <h2 className='fw-bold h4 mb-2'>{heading}</h2>
              <p className='text-muted mb-0'>{body}</p>
            </div>
          ))}
        </Container>
      </section>

      {/* ───────── WHAT IS GREATLY SOCIAL (standing rule 4) ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 940 }}>
          <Row className='align-items-center g-4'>
            <Col md={6}>
              <h2 className='fw-bold mb-3'>What is Greatly Social?</h2>
              <p className='text-muted'>
                Greatly Social is a social media scheduler and content calendar. At Greatly
                Social you can plan the grid and then actually schedule it — the same posts,
                dated, published automatically, across eight networks rather than just Instagram.
              </p>
              <p className='text-muted'>
                Plans start at $9/month and the trial needs no card.
              </p>
              <Button variant='primary' href='/register' className='me-2'>
                Start using Greatly Social free
              </Button>
              <Button variant='outline-primary' href='/plans'>See plans</Button>
            </Col>
            <Col md={6}>
              {/* A real capture of the product, not stock — standing rule 2. */}
              <Image src='/assets/logo/calender.webp' alt='The Greatly Social content calendar'
                     fluid rounded className='shadow-sm' loading='lazy' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── CROSS-LINKS (standing rule 8) ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold h4 mb-3'>More free tools</h2>
          <Row className='g-3'>
            {[['/tools/instagram-post-size', 'Instagram post size', '1080×1350, plus a resizer'],
              ['/tools/instagram-caption-generator', 'Caption generator', 'Every network at once'],
              ['/tools/instagram-bio-generator', 'Instagram bio generator', 'Fits the character limit'],
              ['/tools/instagram-name-generator', 'Instagram name generator', 'Handle ideas that are free'],
              ['/best-time-to-post/instagram', 'Best time to post', 'A schedule for your audience'],
              ['/tools', 'All free tools', 'The full set']].map(([href, title, note]) => (
              <Col md={4} key={href}>
                <a href={href} className='d-block p-3 rounded-3 text-decoration-none h-100'
                   style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                  <div className='fw-semibold'>{title}</div>
                  <div className='text-muted small'>{note}</div>
                </a>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ───────── PRODUCT ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold mb-3'>A planned grid still has to get posted</h2>
          <p className='text-muted fs-5'>
            This tool arranges the pictures. It cannot publish them on Tuesday at nine while you
            are with a customer — that is the part Greatly Social does.
          </p>
          <Row className='g-3 mb-4'>
            {[['Schedule the grid you planned', 'Same order, dated, published automatically.'],
              ['Eight networks, one post', 'A separate caption per network on the same post.'],
              ['A real content calendar', 'Drag to reschedule. Empty days are obvious.'],
              ['AI drafts the captions', 'Describe the business once and get the month.']].map(([t, b]) => (
              <Col md={6} key={t}>
                <div className='d-flex align-items-start gap-2'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <div><div className='fw-semibold'>{t}</div><div className='text-muted small'>{b}</div></div>
                </div>
              </Col>
            ))}
          </Row>
          <Button variant='primary' size='lg' href='/register' className='me-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-primary' size='lg' href='/calendar-view'>See the calendar</Button>
        </Container>
      </section>

      {/* ───────── FAQ (standing rule 5) ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>Common questions</h2>
          <Accordion flush defaultActiveKey='0'>
            {FAQS.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA (standing rule 3) ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>At Greatly Social you can schedule the grid you just planned</h2>
          <p className='lead mb-4'>
            Same order, dated and published automatically across eight networks. Free for 14
            days, no card, plans from $9/month.
          </p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-light' size='lg' href='/calendar-view' className='mb-2'>
            See the calendar
          </Button>
        </div>
      </section>
    </>
  )
}
